import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useDarkMode } from "../../../hooks/useDarkMode";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();

  const isInDashboard = location.pathname.startsWith("/dashboard");
  const paddingClass = isInDashboard ? "py-8" : "pt-24 pb-14";

  const pageBg = isDarkMode ? "bg-gray-950" : "bg-gray-50";
  const cardBg = isDarkMode ? "bg-gray-900" : "bg-white";
  const heading = isDarkMode ? "text-white" : "text-gray-900";
  const text = isDarkMode ? "text-gray-300" : "text-gray-700";
  const subText = isDarkMode ? "text-gray-400" : "text-gray-600";

  return (
    <>
    <section>
  
<p className="sr-only">
Seo Up Rank AI SEO platform official website https://novarsis.ai/
provides SEO audits, keyword insights, and website optimization tools.
</p>



</section>
    <section
      className={`relative min-h-screen w-full overflow-x-hidden px-4 sm:px-6 ${paddingClass} ${pageBg}`}
    >
      {/* ===== BACKGROUND CIRCLES ===== */}
      <div className="absolute -top-24 -left-24 w-60 sm:w-72 h-60 sm:h-72 bg-[#008300]/10 rounded-full blur-3xl" />
      <div className="absolute top-1/3 -right-24 w-72 sm:w-80 h-72 sm:h-80 bg-[#008300]/15 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-1/4 w-56 sm:w-64 h-56 sm:h-64 bg-[#008300]/10 rounded-full blur-3xl" />

      {/* ===== CONTENT CARD ===== */}
      <div
        className={`relative z-10 max-w-4xl xl:max-w-5xl mx-auto rounded-2xl sm:rounded-3xl shadow-2xl ${cardBg}
        p-5 sm:p-8 md:p-10`}
      >
        {/* BACK BUTTON */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-6 text-[#008300] hover:opacity-80 transition text-sm sm:text-base"
        >
          <FaArrowLeft />
          <span className="font-medium">Back</span>
        </button>

        {/* HEADER */}
        <h1
          className={`text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight ${heading}`}
        >
          Privacy Policy
        </h1>
        <p className={`mt-2 text-xs sm:text-sm ${subText}`}>
          Last updated: {new Date().toLocaleDateString()}
        </p>

        {/* INTRO */}
        <div className="mt-6 sm:mt-8 space-y-4">
          <p className={`${text} leading-relaxed text-sm sm:text-base`}>
            <strong>Seo Up Rank</strong> is an AI-powered SEO automation
            platform. We respect your privacy and are committed to protecting
            the data you share with us. This Privacy Policy explains how we
            collect, use, and safeguard information when you use our SEO tools
            and services.
          </p>
        </div>

        {/* CONTENT SECTIONS */}
        {[
          {
            title: "1. Information We Collect",
            content: (
              <>
                <p className={`${text} text-sm sm:text-base`}>
                  We may collect the following information:
                </p>
                <ul
                  className={`list-disc pl-5 sm:pl-6 mt-3 space-y-2 ${text} text-sm sm:text-base`}
                >
                  <li>
                    <strong>Account Details:</strong> Name, email, phone
                  </li>
                  <li>
                    <strong>SEO Usage Data:</strong> Reports, audits, metrics
                  </li>
                  <li>
                    <strong>Website Data:</strong> URLs & metadata
                  </li>
                  <li>
                    <strong>Device Info:</strong> IP, browser, login logs
                  </li>
                </ul>
              </>
            ),
          },
          {
            title: "2. How We Use Your Data",
            content: (
              <ul
                className={`list-disc pl-5 sm:pl-6 space-y-2 ${text} text-sm sm:text-base`}
              >
                <li>AI-driven SEO analysis</li>
                <li>Optimization reports</li>
                <li>Improve algorithm accuracy</li>
                <li>Account security</li>
              </ul>
            ),
          },
          {
            title: "3. AI & Website Crawling",
            content: (
              <ul
                className={`list-disc pl-5 sm:pl-6 space-y-2 ${text} text-sm sm:text-base`}
              >
                <li>User-submitted websites only</li>
                <li>robots.txt respected</li>
                <li>Recommendations are advisory</li>
              </ul>
            ),
          },
          {
            title: "4. Data Security",
            content: (
              <>
                <p className={`${text} text-sm sm:text-base`}>
                  We use industry-standard protections:
                </p>
                <ul
                  className={`list-disc pl-5 sm:pl-6 mt-3 space-y-2 ${text} text-sm sm:text-base`}
                >
                  <li>Encrypted storage</li>
                  <li>HTTPS communication</li>
                  <li>OTP authentication</li>
                </ul>
              </>
            ),
          },
          {
            title: "5. Data Sharing",
            content: (
              <ul
                className={`list-disc pl-5 sm:pl-6 space-y-2 ${text} text-sm sm:text-base`}
              >
                <li>No data selling</li>
                <li>Trusted service providers only</li>
                <li>Legal compliance</li>
              </ul>
            ),
          },
          {
            title: "6. Your Rights",
            content: (
              <ul
                className={`list-disc pl-5 sm:pl-6 space-y-2 ${text} text-sm sm:text-base`}
              >
                <li>Access or delete data</li>
                <li>Request corrections</li>
                <li>Withdraw consent</li>
              </ul>
            ),
          },
        ].map((section, i) => (
          <div key={i} className="mt-8 sm:mt-10">
            <h2
              className={`text-lg sm:text-xl md:text-2xl font-bold ${heading}`}
            >
              {section.title}
            </h2>
            <div className="mt-3 sm:mt-4">{section.content}</div>
          </div>
        ))}

        {/* CONTACT */}
        <div className="mt-10 sm:mt-12 p-5 sm:p-6 rounded-xl sm:rounded-2xl bg-[#008300]/10">
          <h3 className={`text-lg sm:text-xl font-bold ${heading}`}>
            Contact Us
          </h3>
          <p className={`mt-3 ${text} text-sm sm:text-base`}>
            Email:{" "}
            <a
              href="mailto:info@novarsis.tech"
              className="text-[#008300] font-medium hover:underline break-all"
            >
              info@novarsistech.com


            </a>
          </p>
          <p className={`mt-1 ${text} text-sm sm:text-base`}>
            Address: Vijay Nagar, Indore, Madhya Pradesh
          </p>
        </div>
      </div>
    </section></>
  );
}
